import React, { useEffect, useState } from "react";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent } from "@/components/ui/card";
import Lottie from "lottie-react";
import { motion } from "framer-motion";
import { useRef } from "react";
import { Picture } from "@/components/ui/picture";
import { Helmet } from "react-helmet";

const About = () => {

  // Refs for scroll animations
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section3Ref = useRef(null);

  // Animation Trigger
  const useAnimateOnScroll = (refs, animationClass) => {
    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("animate__animated", animationClass);
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.3 }
      );

      refs.forEach((ref) => {
        if (ref.current) observer.observe(ref.current);
      });

      return () => observer.disconnect();
    }, [refs, animationClass]);
  };

  useAnimateOnScroll(
    [section1Ref, section2Ref, section3Ref],
    "animate__rollIn"
  );

  // Animation variants
  const fadeInVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 1.5 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1.0,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
    <Helmet>
    <title>Mopaossi | A propos</title>
    <meta
      name="description"
      content="Développeur Frontend | No-code| Low-code | UI/UX | Agilité"
    />
    <meta property="og:title" content="Mopaossi | Développeur Créatif" />
    <meta
      property="og:description"
      content="Découvrer un créateur, digitalisation, tech, app, dev : Mes sujets préférés."
    />
    <meta property="og:type" content="website" />
    <link rel="canonical" href="https://cbenji.pages.dev" />
  </Helmet>
    <PageLayout
      title="Mopaossi | A propos"
      description="Découvrer la personne, le collègue, qui suis-je ?"
    >
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto  sm:px-6 lg:px-8 ">
          {/* Hero section with initial content */}
          <div className="flex flex-col lg:flex-row items-center justify-between mb-10">
            <motion.div
              className="lg:w-1/2  lg:mb-0 pr-0 lg:pr-6"
              initial="hidden"
              animate="visible"
              variants={fadeInVariants}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-blue-500 animate-gradient">
                Mopaossi
              </h1>
              <p className="text-xl text-gray-700 mb-6">
                Développeur passionné, créateur d'expériences numériques et
                explorateur de nouvelles technologies.
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  Développeur
                </span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                  No-Code
                </span>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                  Low-Code
                </span>
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                  UI/UX
                </span>
                <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                  Agiliste
                </span>
              </div>
            </motion.div>

            <motion.div
              className="lg:w-1/2 relative"
              initial="hidden"
              animate="visible"
              variants={imageVariants}
            >
              <div className="relative z-10 md:w-90 md:h-90 mx-20  rounded-lg overflow-hidden shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <Picture
                  src="https://hynmctzoemxttjsmvjyz.supabase.co/storage/v1/object/public/images//a-propos.png"
                  alt="Mopaossi image"
                  imgClassName="w-full h-auto object-cover"
                  width={500}
                  height={500}
                />
              </div>
              <div className="absolute top-8 right-8 w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg -z-10"></div>
            </motion.div>
          </div>

          {/* Scrollable sections */}
          <div className="space-y-32 top-8">
            {/* Section 1 */}
            <div
              ref={section1Ref}
              className="flex flex-col lg:flex-row items-center"
            >
              <div className="lg:w-2/3 lg:pr-12 mb-12 lg:mb-0">
                <Card className="border-none shadow-lg overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50">
                  <CardContent className="p-8">
                    <h2 className="text-3xl font-bold mb-4 text-blue-800">
                      Mon Parcours
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      En cours de formation en ingénierie informatique avec une spécialisation en développement,
                      je me suis d'abord intéressé au Front-end avant de m'orienter vers le Full Stack.
                      Passionné par la découverte et l'expérimentation, j'explore constamment Internet pour découvrir et optimiser
                      l'exécution des tâches quotidiennes, que ce soit à travers l'IA ou l'automatisation de Google Workspace.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="lg:w-1/3 flex justify-center">
                <div className="w-24 h-24 md:w-32 md:h-32 bg-blue-600 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                  01
                </div>
              </div>
            </div>

            {/* Section 2 - with offset for staircase effect */}
            <div
              ref={section2Ref}
              className="flex flex-col lg:flex-row items-center lg:ml-12"
            >
              <div className="lg:w-2/3 lg:pr-12 mb-12 lg:mb-0">
                <Card className="border-none shadow-lg overflow-hidden bg-gradient-to-br from-purple-50 to-pink-50">
                  <CardContent className="p-8">
                    <h2 className="text-3xl font-bold mb-4 text-purple-800">
                      Ma Philosophie
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Je crois fermement que la technologie doit servir l'humain
                      et non l'inverse. Cette conviction guide mon approche du
                      développement, où l'expérience utilisateur est au cœur de
                      chaque décision. Pour moi, un bon produit numérique doit
                      être à la fois fonctionnel, intuitif et agréable à
                      utiliser. Je m'efforce constamment d'apprendre et
                      d'intégrer de nouvelles technologies pour rester à la
                      pointe de l'innovation tout en maintenant une perspective
                      critique sur leur utilité réelle.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="lg:w-1/3 flex justify-center">
                <div className="w-24 h-24 md:w-32 md:h-32 bg-purple-600 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                  02
                </div>
              </div>
            </div>

            {/* Section 3 - with more offset for staircase effect */}
            <div
              ref={section3Ref}
              className="flex flex-col lg:flex-row items-center lg:ml-24 "
            >
              <div className="lg:w-2/3 lg:pr-12 mb-12 lg:mb-0">
                <Card className="border-none shadow-lg overflow-hidden bg-gradient-to-br from-green-50 to-teal-50">
                  <CardContent className="p-8">
                    <h2 className="text-3xl font-bold mb-4 text-green-800">
                      Ma Mission
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                      Passionné par l'optimisation des processus internes, je me spécialise dans la création d'applications 
                      qui simplifient le quotidien des équipes non-techniques. Mon objectif est de transformer des processus 
                      complexes en solutions intuitives, permettant aux équipes de se concentrer sur leur cœur de métier 
                      tout en augmentant significativement leur productivité.
                    </p>
                  </CardContent>
                </Card>
              </div>
              <div className="lg:w-1/3 flex justify-center">
                <div className="w-24 h-24 md:w-32 md:h-32 bg-green-600 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                  03
                </div>
              </div>
            </div>
          </div>

          {/* Quote section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-24 my-8 mt-24"
          >
            <blockquote className="text-2xl italic text-gray-700">
              "La créativité, c'est l'intelligence qui s'amuse."
            </blockquote>
            <p className="mt-4 text-gray-500">— Albert Einstein</p>
          </motion.div>

         
        </div>
      </div>
    </PageLayout>
    </>
  );
};

export default About;
